import axios from "axios";
import UserLayoutService from "./userLayout.service";
import { API_URL, HEALTH_URL } from "../config/api";

class AuthService {
  async login(user_name, password) {
    return this.startSession(() =>
      axios.post(`${API_URL}user/login`, { user_name, password })
    );
  }

  async loginAsDemo() {
    return this.startSession(() => axios.post(`${API_URL}user/demo-login`));
  }

  async startSession(request) {
    try {
      const response = await request();
      if (response.status === 200) {
        const { access_token, refresh_token, profile, name, user_name } = response.data;
        this.setUserSession({ profile, name, user_name, access_token });
        if (refresh_token) sessionStorage.setItem("refresh_token", refresh_token);

        // Carregar o layout após o login
        await this.loadUserLayout();
      }
      return response;
    } catch (error) {
      console.error("Login error:", error);
      return error.response;
    }
  }

  // O backend gratuito dorme sem acesso; cutucar a sonda ao abrir o login
  // adianta a partida enquanto a pessoa ainda está lendo a tela.
  wakeUpServer() {
    return axios.get(HEALTH_URL).catch(() => null);
  }

  logout() {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("stateListaAcoes");
    sessionStorage.removeItem("stateListaFiis");
    sessionStorage.removeItem("stateListaFavoritas");
    sessionStorage.removeItem("stateListaFavoritosFiis");
  }

  async register(userData) {
    try {
      const response = await axios.post(`${API_URL}users`, userData);
      if (response.status === 201) {
        const { access_token, refresh_token, profile, name, user_name } = response.data;
        this.setUserSession({ profile, name, user_name, access_token });
        if (refresh_token) sessionStorage.setItem("refresh_token", refresh_token);
      }
      return response;
    } catch (error) {
      console.error("Registration error:", error);
      return error.response;
    }
  }

  setUserSession({ profile, name, user_name, access_token }) {
    sessionStorage.setItem(
      "user",
      JSON.stringify({ profile, name, user_name, access_token })
    );
  }

  async loadUserLayout() {
    const layouts = [
      { name: "ListaAcoes", key: "stateListaAcoes" },
      { name: "ListaFiis", key: "stateListaFiis" },
      { name: "ListaFavoritas", key: "stateListaFavoritas" },
      { name: "ListaFavoritosFiis", key: "stateListaFavoritosFiis" },
    ];

    for (const { name, key } of layouts) {
      try {
        const layout = await UserLayoutService.getLayout(name);
        if (layout) {
          sessionStorage.setItem(key, layout);
        } else {
          sessionStorage.removeItem(key);
        }
      } catch (error) {
        // Quem nunca salvou um layout recebe 404 nas quatro tabelas. Isso é o
        // estado normal do primeiro acesso, não um erro a reportar.
        if (error.response?.status !== 404) {
          console.error(`Error loading user layout for ${name}:`, error);
        }
        sessionStorage.removeItem(key);
      }
    }
  }

  getCurrentUser() {
    const user = JSON.parse(sessionStorage.getItem("user"));
    return user ? user.profile : null;
  }

  getCurrentUsername() {
    const user = JSON.parse(sessionStorage.getItem("user"));
    return user ? user.user_name : null;
  }

  getToken() {
    const user = JSON.parse(sessionStorage.getItem("user"));
    return user ? user.access_token : null;
  }

  getRefreshToken() {
    return sessionStorage.getItem("refresh_token");
  }

  async refresh() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    try {
      const response = await axios.post(`${API_URL}auth/refresh`, null, {
        headers: { Authorization: `Bearer ${refreshToken}` },
      });
      if (response.status === 200) {
        const user = JSON.parse(sessionStorage.getItem("user"));
        user.access_token = response.data.access_token;
        sessionStorage.setItem("user", JSON.stringify(user));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  isAuthenticated() {
    return !!sessionStorage.getItem("user");
  }

  isAdmin() {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) return false;
    return user.profile === "ADMIN";
  }

  getCurrentUserId() {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.sub;
    } catch {
      return null;
    }
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
